// pages/pays/index.jsx
import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { 
  Plus, Edit, Trash2, Search, 
  Globe, Check, X, Building2,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import PaysModal from './PaysModal';
import EditPaysModal from './EditPaysModal';
import DeleteConfirmModal from './DeleteConfirmModal';

export default function PaysListe() {
  const [pays, setPays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPays, setSelectedPays] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [paysToDelete, setPaysToDelete] = useState(null);

  const [stats, setStats] = useState({ TotalPays: 0, Actifs: 0, Inactifs: 0 });

  useEffect(() => {
    fetchPays();
    fetchStats();
  }, []);

  const fetchPays = async () => {
    setLoading(true);
    try {
      const res = await api.get('/pays');
      setPays(res.data.pays || []);
    } catch (err) {
      console.error('Erreur chargement pays:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/pays/stats');
      setStats(res.data.stats || { TotalPays: 0, Actifs: 0, Inactifs: 0 });
    } catch (err) {
      console.error('Erreur stats:', err);
    }
  };

  const handleEditClick = (p) => {
    setSelectedPays(p);
    setShowEditModal(true);
  };

  const handleDeleteClick = (p) => {
    setPaysToDelete(p);
    setShowDeleteModal(true);
  };

  const filteredPays = pays.filter(p =>
    p.Nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.Code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPays.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPays.length / itemsPerPage);

  return (
    <div className="p-3 md:p-5 bg-gray-50 min-h-screen">
      <div className="mb-3">
        <h2 className="text-base md:text-lg font-bold text-gray-800">🌍 Pays</h2>
        <p className="text-[11px] text-gray-400">Gestion des pays (multi-tenant)</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4">
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Total</p><p className="text-lg font-bold text-gray-800">{stats.TotalPays}</p></div>
            <Globe size={18} className="text-emerald-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Actifs</p><p className="text-lg font-bold text-green-600">{stats.Actifs}</p></div>
            <Check size={18} className="text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Inactifs</p><p className="text-lg font-bold text-red-600">{stats.Inactifs}</p></div>
            <X size={18} className="text-red-500" />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row justify-between gap-2 mb-3">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
          <input type="text" placeholder="Rechercher..." className="pl-7 pr-3 py-1.5 border border-gray-200 rounded-lg w-full sm:w-60 text-xs focus:ring-2 focus:ring-emerald-500"
            value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
        </div>
        <button onClick={() => setShowCreateModal(true)} className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs flex items-center justify-center gap-1 transition shadow-sm">
          <Plus size={12} /> Nouveau pays
        </button>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="text-[11px] text-gray-500">
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Nom</th>
                <th className="px-3 py-2 text-left">Fuseau horaire</th>
                <th className="px-3 py-2 text-center">Sociétés</th>
                <th className="px-3 py-2 text-center">Fériés</th>
                <th className="px-3 py-2 text-center">Statut</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="7" className="px-3 py-8 text-center text-gray-400 text-xs">Chargement...</td></tr>
              ) : currentItems.length === 0 ? (
                <tr><td colSpan="7" className="px-3 py-8 text-center text-gray-400 text-xs">Aucun pays</td></tr>
              ) : (
                currentItems.map(p => (
                  <tr key={p.ID_Pays} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-bold">{p.Code}</td>
                    <td className="px-3 py-2">{p.Nom}</td>
                    <td className="px-3 py-2 text-gray-500">{p.FuseauHoraire}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                        <Building2 size={10} /> {p.NombreSocietes || 0}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">{p.NombreFeries || 0}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {p.EstActif ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700"><Check size={10} /> Actif</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500"><X size={10} /> Inactif</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleEditClick(p)} className="p-1 text-blue-500 hover:text-blue-700 transition" title="Modifier"><Edit size={14} /></button>
                        <button onClick={() => handleDeleteClick(p)} className="p-1 text-red-500 hover:text-red-700 transition" title="Supprimer"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-3 py-2 border-t border-gray-100 flex justify-between items-center">
            <p className="text-[10px] text-gray-400">{indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredPays.length)} sur {filteredPays.length}</p>
            <div className="flex gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1 rounded disabled:opacity-40 hover:bg-gray-100"><ChevronLeft size={14} /></button>
              <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1 rounded disabled:opacity-40 hover:bg-gray-100"><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      <PaysModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={fetchPays} />
      <EditPaysModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedPays(null); }} onSuccess={fetchPays} pays={selectedPays} />
      <DeleteConfirmModal isOpen={showDeleteModal} onClose={() => { setShowDeleteModal(false); setPaysToDelete(null); }} onSuccess={fetchPays} pays={paysToDelete} />
    </div>
  );
}